import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

/** Cartão de escolha (um entre vários), com miniatura opcional em cima. */
export function ChoiceCard({
  chosen,
  onClick,
  disabled,
  thumb,
  title,
  description,
}: {
  chosen: boolean;
  onClick: () => void;
  disabled?: boolean;
  thumb?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={chosen}
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-col gap-2 rounded-lg border p-3 text-left hover:border-accent disabled:cursor-not-allowed ${
        chosen ? 'border-accent bg-brand-soft' : 'border-rule bg-sheet'
      }`}
    >
      {thumb}
      <span className="flex items-center justify-between gap-2 text-sm font-medium">
        {title}
        {chosen && <Check size={16} aria-hidden className="shrink-0" />}
      </span>
      {description && <span className="text-xs text-muted">{description}</span>}
    </button>
  );
}

/** Uma linha de ajuste: o nome e as opções lado a lado (uma escolhida). */
export function OptionRow<V extends string>({
  label,
  values,
  value,
  onChange,
  labelOf,
  disabled,
}: {
  label: string;
  values: readonly V[];
  value: V;
  onChange: (v: V) => void;
  labelOf: (v: V) => string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
        {values.map((v) => {
          const chosen = v === value;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={chosen}
              disabled={disabled}
              onClick={() => onChange(v)}
              className={`rounded-md border px-2.5 py-1 text-xs disabled:cursor-not-allowed ${
                chosen
                  ? 'border-accent bg-brand-soft font-medium text-brand-text'
                  : 'border-rule bg-sheet hover:border-control-border'
              }`}
            >
              {labelOf(v)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Miniatura sem cor de cada layout: onde ficam menu, lista e item. */
export function LayoutThumb({ id }: { id: string }) {
  const bar = 'bg-ink/70';
  const soft = 'bg-ink/15';
  const line = (w: string) => <span className={`h-1 rounded-sm ${soft} ${w}`} />;
  const content = (
    <span className="flex flex-1 flex-col gap-1 p-1.5">
      {line('w-2/3')}
      {line('w-full')}
      {line('w-5/6')}
      {line('w-1/2')}
    </span>
  );
  const parts: Record<string, ReactNode> = {
    classic: (
      <>
        <span className={`w-1/4 ${bar}`} />
        {content}
      </>
    ),
    dual: (
      <>
        <span className={`w-[8%] ${bar}`} />
        <span className={`w-1/5 border-r border-ink/20 ${soft}`} />
        {content}
      </>
    ),
    top: (
      <span className="flex flex-1 flex-col">
        <span className={`h-2.5 ${bar}`} />
        <span className={`h-1.5 ${soft}`} />
        {content}
      </span>
    ),
    split: (
      <>
        <span className={`w-[8%] ${bar}`} />
        <span className="flex w-1/3 flex-col gap-1 border-r border-ink/20 p-1.5">
          {line('w-full')}
          {line('w-full')}
          {line('w-full')}
        </span>
        {content}
      </>
    ),
    tabs: (
      <span className="flex flex-1 flex-col">
        <span className={`h-2.5 ${bar}`} />
        <span className="flex gap-0.5 px-1 pt-0.5">
          <span className={`h-1.5 w-6 rounded-t-sm ${soft}`} />
          <span className="h-1.5 w-6 rounded-t-sm bg-ink/30" />
        </span>
        {content}
        <span className={`h-1 ${soft}`} />
      </span>
    ),
    launcher: (
      <span className="flex flex-1 flex-col">
        <span className={`h-2.5 ${bar}`} />
        <span className="grid flex-1 grid-cols-3 gap-1 p-1.5">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className={`rounded-sm ${soft}`} />
          ))}
        </span>
      </span>
    ),
    focus: (
      <span className="flex flex-1 flex-col items-center">
        <span className="mt-1.5 h-1.5 w-1/2 rounded-sm border border-ink/30" />
        <span className="flex w-2/3 flex-1 flex-col">{content}</span>
      </span>
    ),
    sheet: (
      <span className="flex flex-1 flex-col">
        <span className={`h-2 ${bar}`} />
        <span className="flex gap-0.5 p-0.5">
          <span className="h-1.5 w-3 rounded-sm bg-ink/40" />
          <span className={`h-1.5 w-3 rounded-sm ${soft}`} />
          <span className={`h-1.5 w-3 rounded-sm ${soft}`} />
        </span>
        <span className="grid flex-1 grid-cols-4 gap-px bg-ink/15 p-px">
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i} className="bg-sheet" />
          ))}
        </span>
      </span>
    ),
    inspector: (
      <>
        <span className={`w-1/5 ${bar}`} />
        {content}
        <span className={`w-1/4 border-l border-ink/20 ${soft}`} />
      </>
    ),
  };
  return (
    <span aria-hidden className="flex h-16 overflow-hidden rounded-md border border-rule bg-sheet">
      {parts[id] ?? parts.classic}
    </span>
  );
}
